import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** What gets a tooltip: anything with data-tooltip or a title, and icon-only buttons and links (from their aria-label). */
const TARGET = "[data-tooltip], [title]:not(iframe), button, a[href], [role='button'], [role='link'], [role='menuitem']";
const SHOW_DELAY_MS = 450;

const iconOnly = (el: HTMLElement) => !(el.textContent ?? "").trim();

function textFor(el: HTMLElement): string | null {
  const t = el.dataset.tooltip ?? el.getAttribute("title") ?? el.dataset.tipTitle ?? (iconOnly(el) ? el.getAttribute("aria-label") : null);
  return t?.trim() || null;
}

/**
 * One tooltip for the whole app. Hovering (or keyboard-focusing) something that has a title, a data-tooltip, or
 * is an icon-only button shows a styled tooltip after a short delay, so no component needs its own. A title is
 * lifted off while the tooltip shows (and put back after) so the browser's own tooltip doesn't appear as well.
 */
export function Tooltips() {
  const [tip, setTip] = useState<{ text: string; rect: DOMRect } | null>(null);
  useEffect(() => {
    let current: HTMLElement | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const hide = () => {
      clearTimeout(timer);
      if (current?.dataset.tipTitle !== undefined) {
        // Put the title back unless the component has set a new one meanwhile.
        if (!current.hasAttribute("title")) current.setAttribute("title", current.dataset.tipTitle);
        delete current.dataset.tipTitle;
      }
      current = null;
      setTip(null);
    };
    const arm = (el: HTMLElement, delay: number) => {
      if (el === current) return;
      hide();
      const text = textFor(el);
      if (!text || (el as HTMLButtonElement).disabled && !el.dataset.tooltip && !el.hasAttribute("title")) return;
      current = el;
      const title = el.getAttribute("title");
      if (title !== null) {
        el.dataset.tipTitle = title;
        el.removeAttribute("title");
      }
      timer = setTimeout(() => { if (el.isConnected && current === el) setTip({ text, rect: el.getBoundingClientRect() }); }, delay);
    };
    const target = (node: EventTarget | null) => (node instanceof Element ? node.closest<HTMLElement>(TARGET) : null);

    const onOver = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const el = target(e.target);
      if (el) arm(el, SHOW_DELAY_MS); else if (current) hide();
    };
    const onOut = (e: PointerEvent) => {
      if (current && !(e.relatedTarget instanceof Node && current.contains(e.relatedTarget))) hide();
    };
    const onFocus = (e: FocusEvent) => {
      const el = target(e.target);
      if (el && el.matches(":focus-visible")) arm(el, 150);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };

    document.addEventListener("pointerover", onOver);
    document.addEventListener("pointerout", onOut);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", hide);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      hide();
      document.removeEventListener("pointerover", onOver);
      document.removeEventListener("pointerout", onOut);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
    };
  }, []);

  return tip ? <Bubble text={tip.text} rect={tip.rect} /> : null;
}

function Bubble({ text, rect }: { text: string; rect: DOMRect }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Above the element, centred; below it if there's no room; always kept inside the window.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const gap = 8;
    const left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8);
    const top = rect.top - height - gap >= 8 ? rect.top - height - gap : rect.bottom + gap;
    setPos({ left, top });
  }, [rect, text]);
  return (
    // Screen readers already announce the element's name; the tooltip is only visual.
    <div ref={ref} role="tooltip" aria-hidden="true"
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
      className="pointer-events-none fixed z-[70] max-w-xs rounded-lg bg-navy/90 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg backdrop-blur-sm animate-float-in">
      {text}
    </div>
  );
}
